import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

// Port of src/app/directives/outside-click.directive.ts ([outsideClick]).
//
// Directive behavior (mirrored exactly):
// - Listens for 'click' events on `document` (NOT mousedown).
// - The subscription is registered inside setTimeout(0) so the click that
//   caused the element to mount does not immediately trigger the callback.
// - Fires when the click target is neither the element itself nor contained
//   within it (element === target || element.contains(target) is excluded).
// - Note: the Angular directive called emit() with no payload despite the
//   EventEmitter<MouseEvent> typing; here the MouseEvent is passed through,
//   which is strictly more information for the consumer.

export function useOutsideClick<T extends HTMLElement>(
  onOutsideClick: (event: MouseEvent) => void,
  enabled: boolean = true
): RefObject<T | null> {
  const ref = useRef<T>(null);

  // Keep the latest callback without re-registering the listener.
  const callbackRef = useRef(onOutsideClick);
  callbackRef.current = onOutsideClick;

  useEffect(() => {
    if (!enabled) return;

    let listener: ((event: MouseEvent) => void) | undefined;

    const timer = window.setTimeout(() => {
      listener = (event: MouseEvent) => {
        const element = ref.current;
        if (!element) return;
        const clickTarget = event.target as HTMLElement;
        const isOrContains = element === clickTarget || element.contains(clickTarget);
        if (!isOrContains) {
          callbackRef.current(event);
        }
      };
      document.addEventListener('click', listener);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      if (listener) document.removeEventListener('click', listener);
    };
  }, [enabled]);

  return ref;
}
